/**
 * El resultado de una tanda de activación.
 *
 * Lo que esta tarjeta tiene que dejar claro en dos segundos es **lo que salió
 * mal**: cuántas etiquetas no se activaron y por qué. Una línea de empaque
 * que ve "listo" y sigue envasando con veinte etiquetas rechazadas adentro de
 * la caja es exactamente el problema que la activación existe para evitar.
 */
import type { ReactNode } from 'react';

import type { components } from '@/api/schema';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { Table, Td, Th } from '@/ui/Table';
import { shortCode } from './codes';
import {
  outcomeLabelKey,
  outcomeTone,
  sortOutcomes,
  unitStatusKey,
} from './outcomes';

type ActivationResult = components['schemas']['ActivationResultOut'];
type Reject = components['schemas']['ActivationRejectOut'];

/** Reparto de desenlaces de la tanda, siempre en el mismo orden. */
export function OutcomeBreakdown({
  outcomes,
}: {
  outcomes: Record<string, number>;
}): ReactNode {
  const { t, fmt } = useI18n();
  const entries = sortOutcomes(outcomes);
  if (entries.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-2">
      {entries.map(([outcome, total]) => {
        const key = outcomeLabelKey(outcome);
        return (
          <li key={outcome}>
            <Badge tone={outcomeTone(outcome)}>
              {key === null ? outcome : t(key)}
              <span className="font-mono">{fmt.number(total)}</span>
            </Badge>
          </li>
        );
      })}
    </ul>
  );
}

/** Un número grande con su rótulo. Es lo que se mira de lejos en la línea. */
function Figure({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'verified' | 'critical';
}): ReactNode {
  const color =
    tone === 'verified' ? 'text-verified' : tone === 'critical' ? 'text-critical' : 'text-ink';
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className={`mt-0.5 font-mono text-xl font-semibold ${color}`}>{value}</dd>
    </div>
  );
}

export function RejectsTable({ rejects }: { rejects: Reject[] }): ReactNode {
  const { t } = useI18n();

  return (
    <Table caption={t('activation.rejects.title')}>
      <thead>
        <tr>
          <Th>{t('activation.rejects.column.code')}</Th>
          <Th>{t('activation.rejects.column.outcome')}</Th>
          <Th>{t('activation.rejects.column.existingBatch')}</Th>
          <Th>{t('activation.rejects.column.existingStatus')}</Th>
        </tr>
      </thead>
      <tbody>
        {rejects.map((reject) => {
          const label = outcomeLabelKey(reject.outcome);
          const statusKey = unitStatusKey(reject.existing_status);
          return (
            <tr key={reject.code}>
              {/* El código impreso, nunca el `unit_id`: es lo que el operario
                  tiene delante para sacar la etiqueta de la caja. */}
              <Td className="font-mono" title={reject.code}>
                {shortCode(reject.code)}
              </Td>
              <Td>
                <Badge tone={outcomeTone(reject.outcome)}>
                  {label === null ? reject.outcome : t(label)}
                </Badge>
              </Td>
              <Td className="text-ink-muted">
                {reject.existing_batch_id === null || reject.existing_batch_id === undefined
                  ? '—'
                  : `#${reject.existing_batch_id}`}
              </Td>
              <Td className="text-ink-muted">
                {statusKey === null
                  ? (reject.existing_status ?? '—')
                  : t(statusKey)}
              </Td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

export function RunResultCard({
  result,
  activatedSample,
  actions,
}: {
  result: ActivationResult;
  /** Códigos que quedaron activados, para verificarlos como consumidor. */
  activatedSample: string[];
  actions: ReactNode;
}): ReactNode {
  const { t, fmt } = useI18n();
  const rejects = result.rejects ?? [];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{result.client_batch_id}</span>
            {result.replayed ? (
              <Badge tone="neutral">{t('activation.result.replayed')}</Badge>
            ) : (
              <Badge tone="verified">{t('activation.result.fresh')}</Badge>
            )}
          </span>
        }
        description={t('activation.result.elapsed', {
          ms: fmt.number(result.elapsed_ms, 1),
        })}
        actions={actions}
      />
      <CardBody className="space-y-4">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Figure label={t('activation.result.requested')} value={fmt.number(result.requested)} />
          <Figure
            label={t('activation.result.activated')}
            value={fmt.number(result.activated)}
            tone={result.activated > 0 ? 'verified' : 'neutral'}
          />
          <Figure label={t('activation.result.already')} value={fmt.number(result.already)} />
          <Figure
            label={t('activation.result.rejected')}
            value={fmt.number(result.rejected)}
            tone={result.rejected > 0 ? 'critical' : 'neutral'}
          />
        </dl>

        <OutcomeBreakdown outcomes={result.outcomes} />

        {result.replayed && (
          <p className="rounded border border-border bg-surface-sunken p-3 text-sm text-ink-muted">
            {t('activation.result.replayedHint')}
          </p>
        )}

        {!result.cache_invalidated && (
          // Las unidades ESTÁN activadas; lo que puede quedar viejo es la
          // respuesta que ve el consumidor durante el TTL del cache.
          <p className="rounded border border-suspect/30 bg-suspect-soft p-3 text-sm text-suspect">
            {t('activation.result.cacheStale')}
          </p>
        )}

        {activatedSample.length > 0 && (
          <div>
            <p className="text-sm text-ink-muted">{t('activation.result.verifyHint')}</p>
            <ul className="mt-1 flex flex-wrap gap-2">
              {activatedSample.map((code) => (
                <li key={code} className="font-mono text-sm text-ink select-all">
                  {code}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>

      {rejects.length > 0 && (
        <>
          <CardHeader
            title={t('activation.rejects.title')}
            description={
              result.rejected > rejects.length
                ? t('activation.rejects.sample', {
                    shown: fmt.number(rejects.length),
                    total: fmt.number(result.rejected),
                  })
                : t('activation.rejects.description')
            }
          />
          <RejectsTable rejects={rejects} />
        </>
      )}
    </Card>
  );
}
